import { HttpClient, HttpResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Subject, Observable, map, catchError, fromEvent, tap } from "rxjs";

import { saveAs } from 'file-saver';

import { ConfigService } from "../config/config.service";

import { HttpMethod, DownloadFormat } from "./download.enumerations";

export { DownloadFormat, HttpMethod };


@Injectable({
    providedIn: 'root',
})
export class DownloadService {
    downloadSubject: Subject<Response> = new Subject<Response>();

    constructor(private http: HttpClient, private configService: ConfigService) { }

    // #region Retrieve Download Service Call

    // tslint:disable-next-line: no-any
    RetrieveDownload(
        httpMethod: HttpMethod,
        ServiceURL: string,
        // tslint:disable-next-line: no-any
        Parameters: any,
        Format: DownloadFormat
    ): Observable<HttpResponse<Blob> | Event> {
        let headers = new HttpHeaders(
            httpMethod !== HttpMethod.POST_FORMDATA
                ? {
                    'Content-Type': 'application/json',
                }
                : {}
        );

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.JPG:
                headers = headers.set('Accept', 'image/jpeg');
                extension = '.jpg';
                break;
            case DownloadFormat.PNG:
                headers = headers.set('Accept', 'image/png');
                extension = '.png';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
            case DownloadFormat.VCARD:
                headers = headers.set('Accept', 'text/vcard');
                extension = '.vcf';
                break;
            case DownloadFormat.DOCX:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
                extension = '.docx';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const queryString = Object.keys(Parameters)
                .reduce((prev: string[], cur: string) => {
                    prev.push(`${encodeURIComponent(cur)}=${encodeURIComponent(Parameters[cur] || '')}`);

                    return prev;
                }, [])
                .join('&');

            const url = this.configService.hostAddress + `${ServiceURL}?${queryString}`;

            responseObs = this.http.get(url, { headers, observe: 'response', responseType: 'blob' });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(this.configService.hostAddress + ServiceURL, Parameters, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                if (blob) {
                    saveAs(blob, filename);
                }

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    throw new Error('Unknown error: ' + (body as string));
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion

    // #region Retrieve File Service Call

    RetrieveFile(
        httpMethod: HttpMethod,
        FileURL: string,
        Format: DownloadFormat,
        Filename?: string
    ): Observable<HttpResponse<Blob> | Event> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'HSAAdmissions',
        });

        let extension = '';

        switch (Format) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.JPG:
                headers = headers.set('Accept', 'image/jpeg');
                extension = '.jpg';
                break;
            case DownloadFormat.PNG:
                headers = headers.set('Accept', 'image/png');
                extension = '.png';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
            case DownloadFormat.VCARD:
                headers = headers.set('Accept', 'text/vcard');
                extension = '.vcf';
                break;
            case DownloadFormat.DOCX:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
                extension = '.docx';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        if (httpMethod === HttpMethod.GET) {
            // Process request as a GET method
            const url = this.configService.hostAddress + `${FileURL}`;

            responseObs = this.http.get(url, { headers, observe: 'response', responseType: 'blob' });
        } else {
            // Process request as a POST method
            responseObs = this.http.post(this.configService.hostAddress + FileURL, null, {
                headers,
                observe: 'response',
                responseType: 'blob',
            });
        }

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = Filename || 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                if (blob) {
                    saveAs(blob, filename);
                }

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    throw new Error('Unknown error: ' + (body as string));
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion

    // #region Run Report Service Call

    RunReport(
        AuthToken: string,
        CurrentRole: string,
        ReportID: number,
        ReportType: DownloadFormat,
        ReportFileName: string,
        ApplicationSchoolYear: string,
        UseDateRestriction: boolean,
        DateBegin: Date,
        DateEnd: Date,
        DateRestrictionType: number,
        AsDistrictOffice: boolean
    ): Observable<HttpResponse<Blob> | Event> {
        let headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ApplicationName: 'HSAAdmissions',
        });

        let extension = '';

        switch (ReportType) {
            case DownloadFormat.PDF:
                headers = headers.set('Accept', 'application/pdf');
                extension = '.pdf';
                break;
            case DownloadFormat.JPG:
                headers = headers.set('Accept', 'image/jpeg');
                extension = '.jpg';
                break;
            case DownloadFormat.PNG:
                headers = headers.set('Accept', 'image/png');
                extension = '.png';
                break;
            case DownloadFormat.Excel:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                extension = '.xlsx';
                break;
            case DownloadFormat.ZIP:
                headers = headers.set('Accept', 'application/zip');
                extension = '.zip';
                break;
            case DownloadFormat.VCARD:
                headers = headers.set('Accept', 'text/vcard');
                extension = '.vcf';
                break;
            case DownloadFormat.DOCX:
                headers = headers.set('Accept', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
                extension = '.docx';
                break;
        }

        let responseObs: Observable<HttpResponse<Blob>>;

        responseObs = this.http.post(
            this.configService.hostAddress + 'Api/Services/Report/RunReport',
            {
                AuthToken,
                CurrentRole,
                ReportID,
                ApplicationSchoolYear,
                UseDateRestriction,
                DateBegin: DateBegin != null ? DateBegin.toJSON() : null,
                DateEnd: DateEnd != null ? DateEnd.toJSON() : null,
                DateRestrictionType,
                AsDistrictOffice,
                Attachment: true,
            },
            { headers, observe: 'response', responseType: 'blob' }
        );

        // Process the response on the mapping/subscription
        return responseObs.pipe(
            map((resp) => {
                const blob = resp.body;

                const contentdistribution = resp.headers.get('content-disposition');
                let filename: string = 'Output' + extension;

                if (contentdistribution) {
                    const matches = contentdistribution.match(/(?:filename="?(.*?)"?;|filename="?(.*?)"?$)/);
                    if (matches && matches.length > 1) {
                        filename = matches[1];
                    }
                }

                // FileSaver.saveAs(blob, ReportFileName);
                if (blob) {
                    saveAs(blob, ReportFileName);
                }

                return resp;
            }),
            catchError((error) => {
                const body = error.body || error.error || error;

                if (!(body instanceof Blob)) {
                    throw new Error('Unknown error: ' + (body as string));
                }

                const reader = new FileReader();
                const loadend = fromEvent(reader, 'loadend');

                reader.readAsText(body);

                return loadend.pipe(
                    tap(() => {
                        throw new Error((reader.result as string).replace(/(^"|"$)/g, ''));
                    })
                );
            })
        );
    }

    // #endregion
}
